import './TiersTable.scss'
import { CommonTooltip, DynamicImage } from '@/components';
import {
  CooldownPeriodTooltip,
  FCFSLotteryTooltip,
  FPRequirementsTooltip,
  GuaranteedAllocationTooltip,
  SurpriseLotteryTooltip
} from './tooltips';
import { ReactComponent as Tickets } from '@assets/tiers/tickets.svg';
import { usePlatformSettings } from '@hooks/usePlatformSettings';
import React, { useMemo } from 'react';
import { numberToCurrency } from '@utils/balanceFormatter';
import { TiersTableMobile } from '@components/common/TiersTableMobile'
import useScreenSize from 'use-screen-size'
import { TierEntity } from '@api/tiers/types'
import { Image } from 'react-bootstrap'
import classNames from 'classnames'

const TierRow = ({ tier, tickets, highlight }: { tier: TierEntity, tickets?: string, highlight?: boolean }) => (
  <>
    <div className={classNames('tiers-table__col tiers-table__col--with-image tiers-table__col--tier', {
      'tiers-table__col--highlight': highlight
    })}>
      <Image src={tier.icon} />
      {tier.name}
    </div>
    <div
      className={classNames('tiers-table__col', {
        'tiers-table__col--highlight': highlight,
      })}
    >
      {numberToCurrency(tier.min_flame_power)} +
    </div>
    <div
      className={classNames('tiers-table__col text-center', {
        'tiers-table__col--highlight': highlight,
      })}
    >
      {tier.allocation_multiplier
        ? tier.allocation_multiplier === 1
          ? 'Base allocation'
          : <span className='tiers-multiplier--rotate gradient-text gradient-text--purple'>{tier.allocation_multiplier}x</span>
        : 'n/a'}
    </div>
    <div
      className={classNames('tiers-table__col', {
        'tiers-table__col--highlight': highlight,
        'tiers-table__col--surprise': !tickets,
        'tiers-table__col--with-image': tickets
      })}
    >
      {!tickets ? (
        <>
          <DynamicImage path="tiers/present.svg" />
          <span>
            <span className="purple-text">10 lucky winners{' '}</span>
            will get IDO launch prize
          </span>
        </>
      ) : (
        <>
          <Tickets />
          {tickets}
        </>
      )}
    </div>
  </>
)

export const TiersTable = () => {
  const { settings } = usePlatformSettings()
  const tiers = useMemo(() => settings?.tiers ?? [], [settings])

  const screenSize = useScreenSize()
  const mobile = screenSize.width < 992;

  return (
    <>
      {!mobile
        ? (<div className="tiers-table">
          <div className="tiers-table__head">
            <div className="tiers-table__col">Access</div>
            <div className="tiers-table__col">Tier</div>
            <div className="tiers-table__col">
              Flame Power
              Requirement
              <CommonTooltip
                id="flame-power-requirement"
                triggerClass="flame-power-requirement"
                tooltipClass="tiers-tooltip"
              >
                <FPRequirementsTooltip />
              </CommonTooltip>
            </div>
            <div className="tiers-table__col">Base Allocation Multiplier per Tier</div>
            <div className="tiers-table__col">Lottery Tickets (chances to win)</div>
            <div className="tiers-table__col">
              Cooldown period
              <CommonTooltip
                id="cooldown-period"
                triggerClass="cooldown-period"
                tooltipClass="tiers-tooltip"
                placement="left-start"
              >
                <CooldownPeriodTooltip />
              </CommonTooltip>
            </div>
          </div>
          <div className="tiers-table__body">
            <div className="tiers-table__col tiers-table__col--access">
              Surprise
              <br />
              lottery
              <CommonTooltip
                id="surprise-lottery"
                triggerClass="surprise-lottery"
                tooltipClass="tiers-tooltip"
                placement="right-start"
              >
                <SurpriseLotteryTooltip />
              </CommonTooltip>
            </div>
            <TierRow tier={tiers.find(t => t.name === 'LIGHT')!} />
            <div className="tiers-table__col rowspan-1-4 tiers-table__col--cooldown">
              <DynamicImage path="tiers/timer.svg" />
              <span className="text">
                <span className='purple-text'>7 days{' '}</span>
                after <br />
                successful IDO <br />
                participation
              </span>
            </div>
            <div className="tiers-table__col rowspan-2-4 tiers-table__col--access">
              FCFS
              <br />
              whitelist
              <br />
              lottery
              <CommonTooltip
                id="fcfs-lottery"
                triggerClass="fcfs-lottery"
                tooltipClass="tiers-tooltip"
                placement="right-start"
              >
                <FCFSLotteryTooltip />
              </CommonTooltip>
            </div>
            <TierRow tier={tiers.find(t => t.name === 'SPARK')!} tickets="1-9" />
            <TierRow tier={tiers.find(t => t.name === 'SMOKE')!} tickets="10-19" />
            <TierRow tier={tiers.find(t => t.name === 'FIRE')!} tickets="20-49" />
            <TierRow tier={tiers.find(t => t.name === 'FLAME')!} tickets="50+" highlight />
            <div className="tiers-table__col tiers-table__col--non-cooldown tiers-table__col--highlight">
              <DynamicImage path='tiers/cup.svg' />
              <span className='text'>
                Not applicable <br />
                to <span className='purple-text'>{' '}TOP{' '}</span> 10 on the
                <span className='purple-text fst-italic text-decoration-underline'>{' '}FLAME
                  Leaderboard{' '}</span>
              </span>
            </div>
            <div className="tiers-table__col tiers-table__col--access">
              Guaranteed
              <br />
              allocation
              <CommonTooltip
                id="guaranteed-allocation"
                triggerClass="guaranteed-allocation"
                tooltipClass="tiers-tooltip"
                placement="right-start"
              >
                <GuaranteedAllocationTooltip />
              </CommonTooltip>
            </div>
            <div className="tiers-table__col colspan-2-5 tiers-table__col--leaders">
              <span className='text text-center'>
                Top 100 on
                <span className='purple-text fst-italic text-decoration-underline'>
                  {' '}FLAME Leaderboard{' '}
                </span> and
                <span className='purple-text fst-italic text-decoration-underline'>
                  {' '}Hiro NFT{' '}
                </span> holders. Amount of max
                allocation depends on Tier.
              </span>
            </div>
          </div>
        </div>)
        : (<TiersTableMobile tiers={tiers} />)
      }
    </>
  )
}
